import axios, { AxiosInstance, AxiosError } from 'axios';

// const API_BASE_URL = (() => {
//   const envUrl = process.env.REACT_APP_API_URL?.trim();
//   if (envUrl) return envUrl;
//   try {
//     const origin = window.location.origin;
//     const u = new URL(origin);
//     const p = u.port || '';
//     if (p === '3001' || p === '3010') return 'http://localhost:3000/api';
//     return `${origin}/api`;
//   } catch {
//     return 'http://localhost:3000/api';
//   }
// })();

const API_BASE_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return '/api';
})();


class ApiService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Request interceptor to add auth token
        this.axiosInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                const selectedCompanyId = localStorage.getItem('selectedCompanyId');
                const isGet = String(config.method || 'get').toLowerCase() === 'get';
                const eligiblePath = (config.url || '').startsWith('/sap/') || (config.url || '').startsWith('/item-master') || (config.url || '').startsWith('/schedules');
                if (selectedCompanyId && isGet && eligiblePath) {
                    config.params = config.params || {};
                    if (config.params && typeof config.params === 'object' && !('companyId' in (config.params as any))) {
                        (config.params as any).companyId = Number(selectedCompanyId);
                    }
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor for error handling
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                const requestUrl = String(error.config?.url || '');
                const isAuthenticationRequest =
                    requestUrl.includes('/auth/login') ||
                    requestUrl.includes('/auth/first-time-password-change');

                if (error.response?.status === 401 && !isAuthenticationRequest) {
                    // A 401 from an authenticated request means the session
                    // expired. Login failures must stay on the login page so
                    // the form can display the actual credential error.
                    localStorage.removeItem('token');
                    if (window.location.pathname !== '/login') {
                        window.location.replace('/login');
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    // Generic methods
    async get<T>(url: string, params?: any): Promise<T> {
        const response = await this.axiosInstance.get<T>(url, { params });
        return response.data;
    }

    async post<T>(url: string, data?: any): Promise<T> {
        const response = await this.axiosInstance.post<T>(url, data);
        return response.data;
    }

    async put<T>(url: string, data?: any): Promise<T> {
        const response = await this.axiosInstance.put<T>(url, data);
        return response.data;
    }

    async delete<T>(url: string, config?: { data?: any }): Promise<T> {
        const response = await this.axiosInstance.delete<T>(url, config);
        return response.data;
    }

    // Auth methods
    async login(username: string, password: string): Promise<any> {
        return this.post('/auth/login', { username, password });
    }

    async register(userData: any): Promise<any> {
        return this.post('/auth/register', userData);
    }

    async firstTimePasswordChange(userId: number, currentPassword: string, newPassword: string): Promise<any> {
        return this.post('/auth/first-time-password-change', { userId, currentPassword, newPassword });
    }

    async getProfile(): Promise<any> {
        return this.get('/auth/profile');
    }

    // PR methods
    async createPR(prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/pr', final);
    }

    async createPRDraft(prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/pr/draft', final);
    }

    async updatePRDraft(prId: number, prData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(prData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.put(`/pr/draft/${prId}`, final);
    }

    async discardPRDraft(prId: number): Promise<any> {
        return this.delete(`/pr/draft/${prId}`);
    }

    async getPR(id: number): Promise<any> {
        return this.get(`/pr/${id}`);
    }

    async getMyPRs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/pr/my-prs', params);
    }

    async getMyDrafts(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/pr/my-drafts', params);
    }

  async getPendingApprovals(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/pending-approvals', params);
  }

  async getApproverPRs(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/approver-tracking', params);
  }

  async getApprovalActivityStats(): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
    return this.get('/pr/my-approval-activity', params);
  }

  async getApprovalTiming(params?: { from?: string; to?: string }): Promise<any> {
    const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
    return this.get('/pr/approval-timing', { ...(params || {}), ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}) });
  }

  async createPOFromPR(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/create-po`);
  }

  async reinitiatePR(prId: number, reason: string): Promise<any> {
    return this.post(`/pr/${prId}/reinitiate`, { reason });
  }

  async submitPR(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/submit`);
  }

  async movePRToCompleted(prId: number): Promise<any> {
    return this.post(`/pr/${prId}/move-to-completed`);
  }

  async getPRPostingAttempts(prId: number): Promise<any> {
    return this.get(`/pr/${prId}/posting-attempts`);
  }

  subscribePRStatus(prId: number): EventSource {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    const url = `${API_BASE_URL}/pr/${prId}/subscribe${q}`;
    const es = new EventSource(url);
    return es;
  }

  // File attachments methods
  async uploadPRAttachments(prId: number, files: File[], onUploadProgress?: (e: any) => void): Promise<any> {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    const response = await this.axiosInstance.post(`/files/upload/${prId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  }

  async uploadSingleAttachment(prId: number, file: File, onUploadProgress?: (e: any) => void): Promise<any> {
    const form = new FormData();
    form.append('files', file);
    const response = await this.axiosInstance.post(`/files/upload/${prId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
    return response.data;
  }

  async getPRAttachments(prId: number): Promise<any> {
    return this.get(`/files/pr/${prId}`);
  }

  async deleteAttachment(attachmentId: number): Promise<any> {
    return this.delete(`/files/${attachmentId}`);
  }

  getAttachmentDownloadUrl(attachmentId: number): string {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/files/download/${attachmentId}${q}`;
  }

  getAttachmentViewUrl(attachmentId: number): string {
    const token = localStorage.getItem('token') || '';
    const q = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/files/view/${attachmentId}${q}`;
  }

  async fetchAttachmentBlob(attachmentId: number): Promise<{ blob: Blob; contentType: string }> {
    const response = await this.axiosInstance.get(`/files/download/${attachmentId}`, { responseType: 'blob' });
    const ct = String((response.headers as any)['content-type'] || 'application/octet-stream');
    return { blob: response.data as Blob, contentType: ct };
  }

    // Approval methods
    async processApproval(prId: number, action: string, comments?: string): Promise<any> {
        return this.post(`/approval/${prId}/process`, { action, comments });
    }

    async getWorkflowStatus(prId: number): Promise<any> {
        return this.get(`/approval/${prId}/workflow`);
    }

    async getApprovalHistory(prId: number): Promise<any> {
        return this.get(`/approval/${prId}/history`);
    }

  async resolveApprovalToken(token: string): Promise<any> {
    return this.get(`/approval/token/${encodeURIComponent(token)}`);
  }

    // Approval workflow settings
    async listFacilityTypes(companyId?: number): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.get('/user-management/facility-types', { companyid: companyId ?? selectedCompanyId });
    }

    async createFacilityType(name: string, companyId?: number): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.post('/user-management/facility-types', {
            facilitytype: name,
            companyid: companyId ?? selectedCompanyId,
        });
    }

    async listApprovalWorkflows(params?: { documentType?: 'PR' | 'PO'; requisitionType?: string; allocationType?: string; companyId?: number; flowName?: string }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documentType) final.documentType = 'PR';
        if (!final.companyId && selectedCompanyId) final.companyId = selectedCompanyId;
        return this.get('/user-management/approval-workflows', final);
    }

    async getApprovalWorkflowPendingCounts(params?: { documentType?: 'PR' | 'PO'; requisitionType?: string; allocationType?: string; companyId?: number; flowName?: string }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documentType) final.documentType = 'PR';
        if (!final.companyId && selectedCompanyId) final.companyId = selectedCompanyId;
        return this.get('/user-management/approval-workflows/pending-counts', final);
    }

    async createApprovalWorkflow(data: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(data || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-workflows', final);
    }

    async updateApprovalWorkflow(id: number, data: any): Promise<any> {
        return this.put(`/user-management/approval-workflows/${id}`, data);
    }

    async deleteApprovalWorkflow(id: number, futureOnly = false): Promise<any> {
        return this.delete(`/user-management/approval-workflows/${id}${futureOnly ? '?futureOnly=true' : ''}`);
    }

    async reorderApprovalWorkflows(payload: { documenttype?: 'PR' | 'PO'; requisitiontype: string; allocationtype: string; orderedIds: number[]; flowname?: string; companyid?: number; facilitytype?: string | null; cycle?: string | null; allocationcode?: string | null; futureOnly?: boolean }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(payload || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-workflows/reorder', final);
    }

    // Bulk workflow management operations
    async bulkAssignUsersToWorkflows(payload: {
        workflowIds: number[];
        userIds: number[];
        role: 'initiator' | 'approver';
        stageNumber?: number;
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-assign-users', payload);
    }

    async bulkRemoveUsersFromWorkflows(payload: {
        workflowIds: number[];
        userIds: number[];
        role?: 'initiator' | 'approver';
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-remove-users', payload);
    }

    async bulkReassignUsers(payload: {
        workflowIds: number[];
        fromUserIds: number[];
        toUserIds: number[];
        preserveRoles?: boolean;
    }): Promise<any> {
        return this.post('/user-management/approval-workflows/bulk-reassign-users', payload);
    }

    async bulkModifyWorkflowStage(payload: {
        workflowIds: number[];
        stageNumber: number;
        updates: {
            rule?: 'ANY' | 'ALL';
            addUsers?: number[];
            removeUsers?: number[];
            replaceUsers?: number[];
        };
    }): Promise<any> {
        return this.put('/user-management/approval-workflows/bulk-modify-stage', payload);
    }

    async getWorkflowsByProject(projectCode: string): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.get('/user-management/approval-workflows/by-project', { projectCode, companyId: selectedCompanyId });
    }

    async getUserWorkflows(userId: number): Promise<any> {
        return this.get(`/user-management/users/${userId}/workflows`);
    }

    async getWorkflowStats(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
        return this.get('/user-management/approval-workflows/stats', { companyId: selectedCompanyId });
    }


    // Approval initiators
    async setApprovalInitiators(payload: { documenttype?: 'PR' | 'PO'; requisitiontype: string; allocationtype: string; userids: number[]; flowname?: string; companyid?: number; facilitytype?: string | null; cycle?: string | null; allocationcode?: string | null }): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(payload || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/user-management/approval-initiators', final);
    }

    async listApprovalInitiators(params: { documenttype?: 'PR' | 'PO'; requisitiontype?: string; allocationtype?: string; flowname?: string; companyid?: number; facilitytype?: string | null; cycle?: string | null; allocationcode?: string | null } = {}): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(params || {}) } as any;
        if (!final.documenttype) final.documenttype = 'PR';
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.get('/user-management/approval-initiators', final);
    }

    async getUserAvailableFlowOptions(userId: number): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params: any = { userId };
        if (selectedCompanyId) params.companyId = selectedCompanyId;
        return this.get('/user-management/approval-flow-options', params);
    }

    // Reusable approval templates and project/cost-center assignments
    async listApprovalTemplates(params: any = {}): Promise<any> {
        return this.get('/user-management/approval-templates', params);
    }

    async getApprovalTemplate(id: number): Promise<any> {
        return this.get(`/user-management/approval-templates/${id}`);
    }

    async createApprovalTemplate(data: any): Promise<any> {
        return this.post('/user-management/approval-templates', data);
    }

    async updateApprovalTemplate(id: number, data: any): Promise<any> {
        return this.put(`/user-management/approval-templates/${id}`, data);
    }

    async listApprovalScopeAssignments(params: any = {}): Promise<any> {
        return this.get('/user-management/approval-scope-assignments', params);
    }

    async createApprovalScopeAssignment(data: any): Promise<any> {
        return this.post('/user-management/approval-scope-assignments', data);
    }

    async deactivateApprovalScopeAssignment(id: number): Promise<any> {
        return this.delete(`/user-management/approval-scope-assignments/${id}`);
    }

    async previewApprovalTemplate(data: any): Promise<any> {
        return this.post('/user-management/approval-templates/preview', data);
    }

    // Budget methods
    async checkBudget(budgetData: any): Promise<any> {
        return this.post('/budget/check', budgetData);
    }

  async getBudgetUtilization(filters?: any): Promise<any> {
      return this.get('/budget/utilization', filters);
  }

  async createBudget(payload: { fiscalyear: number; allocationtype: string; allocationcode: string; accountcode: string; budgetamount: number; startdate: string | Date; enddate: string | Date }): Promise<any> {
    return this.post('/budget', payload);
  }

  async listClassifications(activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/classifications', params);
  }

  async createClassification(payload: { code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/classifications', payload);
  }

  async listCostGroups(classificationId?: number, activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (typeof classificationId === 'number') params.classificationId = classificationId;
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/cost-groups', params);
  }

  async createCostGroup(payload: { classificationid: number; code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/cost-groups', payload);
  }

  async listProjectCostGroups(costGroupId?: number, activeOnly?: boolean): Promise<any> {
    const params: any = {};
    if (typeof costGroupId === 'number') params.costGroupId = costGroupId;
    if (activeOnly) params.activeOnly = true;
    return this.get('/budget/hierarchy/project-cost-groups', params);
  }

  async createProjectCostGroup(payload: { costgroupid: number; code: string; name: string }): Promise<any> {
    return this.post('/budget/hierarchy/project-cost-groups', payload);
  }

  async listProjectBudgetLines(projectCode: string, year?: number): Promise<any> {
    const params: any = { projectCode };
    if (typeof year === 'number') params.year = year;
    return this.get('/budget/project-lines', params);
  }

  async upsertProjectBudgetLine(payload: { projectcode: string; year: number; classificationid: number; costgroupid: number; projectcostgroupid: number; originalbudget: number; internalchanges?: number; clientchanges?: number; note?: string }): Promise<any> {
    return this.post('/budget/project-lines/upsert', payload);
  }

  async getProjectBudgetHeader(projectCode: string): Promise<any> {
    const params: any = { projectCode };
    return this.get('/budget/project-header', params);
  }

  async upsertProjectBudgetHeader(payload: { projectcode: string; startdate?: string | Date | null; enddate?: string | Date | null; contractnumber?: string | null; totalvalue?: number | null }): Promise<any> {
    return this.post('/budget/project-header/upsert', payload);
  }

  async getBudgetsByAllocation(allocationType: string, allocationCode: string, fiscalYear?: number): Promise<any> {
    const params: any = {};
    if (fiscalYear) params.fiscalYear = fiscalYear;
    return this.get(`/budget/${encodeURIComponent(allocationType)}/${encodeURIComponent(allocationCode)}`, params);
  }

    async getLogReports(params?: { store?: 'db'|'file'; source?: string; date?: string; level?: string; limit?: number; search?: string }): Promise<any> {
        return this.get('/reports/logs', params);
    }

    async getApprovalTracking(params?: {
        date?: string;
        startDate?: string;
        endDate?: string;
        prNumber?: string;
        requesterUserId?: number;
        status?: string;
        companyId?: number;
    }): Promise<any> {
        return this.get('/reports/approval-tracking', params);
    }

    async exportApprovalTracking(params?: {
        date?: string;
        startDate?: string;
        endDate?: string;
        prNumber?: string;
        requesterUserId?: number;
        status?: string;
        companyId?: number;
    }): Promise<void> {
        const queryParams = new URLSearchParams();
        if (params?.date) queryParams.append('date', params.date);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.prNumber) queryParams.append('prNumber', params.prNumber);
        if (params?.requesterUserId) queryParams.append('requesterUserId', String(params.requesterUserId));
        if (params?.status) queryParams.append('status', params.status);
        if (params?.companyId) queryParams.append('companyId', String(params.companyId));

        const token = localStorage.getItem('token');
        const url = `${this.axiosInstance.defaults.baseURL}/reports/approval-tracking/export?${queryParams.toString()}`;

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) throw new Error('Export failed');

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `approval-tracking-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    async getPRDetailedReport(params?: { startDate?: string; endDate?: string; prNumber?: string; status?: string; requisitionType?: string; allocationType?: string; companyId?: number; projectCodes?: string[]; costCenterCodes?: string[] }): Promise<any> {
        return this.get('/reports/pr-detailed', params);
    }

    async exportPRDetailedReport(params?: { startDate?: string; endDate?: string; prNumber?: string; status?: string; requisitionType?: string; allocationType?: string; companyId?: number; projectCodes?: string[]; costCenterCodes?: string[] }, format: 'csv' | 'xlsx' = 'xlsx'): Promise<void> {
        const queryParams = new URLSearchParams({ format });
        Object.entries(params || {}).forEach(([key, value]) => {
            if (Array.isArray(value)) value.forEach((item) => queryParams.append(key, String(item)));
            else if (value !== undefined && value !== '') queryParams.append(key, String(value));
        });
        const token = localStorage.getItem('token');
        const url = `${this.axiosInstance.defaults.baseURL}/reports/pr-detailed/export?${queryParams.toString()}`;
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) throw new Error('PR detailed report export failed');
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `pr-detailed-${new Date().toISOString().slice(0, 10)}.${format}`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(downloadUrl);
    }

    // SAP B1 methods
  async getItems(): Promise<any> {
    return this.get('/sap/items');
  }

    async getServiceItems(): Promise<any> {
        return this.get('/sap/service-items');
    }

  async getProjects(): Promise<any> {
    return this.get('/sap/projects');
  }

  async getDistributionRulesByLevel(level?: number): Promise<any> {
    const params: any = {};
    if (typeof level === 'number') params.level = level;
    return this.get('/sap/distribution-rules', params);
  }

  async listProjects(companyId?: number): Promise<any> {
    const params = companyId ? { companyId } : undefined;
    return this.get('/projects', params);
  }

  async createProject(payload: { companyid?: number | null; code: string; name?: string; active?: boolean }): Promise<any> {
    return this.post('/projects', payload);
  }

  async updateProject(id: number, payload: { code?: string; name?: string; active?: boolean; data?: any }): Promise<any> {
    return this.put(`/projects/${id}`, payload);
  }

  async deleteProject(id: number): Promise<any> {
    return this.delete(`/projects/${id}`);
  }

  async getProjectByCode(code: string, companyId?: number): Promise<any> {
    const params: any = { code };
    if (companyId) params.companyId = companyId;
    return this.get('/projects/by-code', params);
  }

    async getAccounts(): Promise<any> {
        return this.get('/sap/accounts');
    }

  async getVendors(): Promise<any> {
      return this.get('/sap/vendors');
  }

  async getWarehouses(): Promise<any> {
      return this.get('/sap/warehouses');
  }

  async getItemGroups(params?: { companyId?: number; code?: string }): Promise<any> {
    return this.get('/sap/item-groups', params);
  }

    // User Management methods (Admin-only for most)
    async getUsers(params?: { role?: string; active?: boolean }): Promise<any> {
        return this.get('/user-management/users', params);
    }

    async getUserById(id: number): Promise<any> {
        return this.get(`/user-management/users/${id}`);
    }

    async updateUser(id: number, data: {
        email?: string;
        fullname?: string;
        department?: string;
        role?: string;
        isactive?: boolean;
        sapusercode?: string;
    }): Promise<any> {
        return this.put(`/user-management/users/${id}`, data);
    }

    async deleteUser(id: number): Promise<any> {
        return this.delete(`/user-management/users/${id}`);
    }

    async deactivateUser(id: number): Promise<any> {
        return this.post(`/user-management/users/${id}/deactivate`, {});
    }

    async resetUserPassword(id: number, newPassword: string): Promise<any> {
        return this.post(`/user-management/users/${id}/reset-password`, { newPassword });
    }

    // Company Management methods
    async getCompanies(active?: boolean): Promise<any> {
        const params: any = {};
        if (active !== undefined) params.active = active;
        return this.get('/company-management/companies', params);
    }

    async getMyCompanies(active?: boolean): Promise<any> {
        const params: any = {};
        if (active !== undefined) params.active = active;
        return this.get('/company-management/my-companies', params);
    }

    async createCompany(payload: { companycode: string; companyname: string; databasename: string; isactive?: boolean }): Promise<any> {
        return this.post('/company-management/companies', payload);
    }

    async updateCompany(id: number, payload: { companycode?: string; companyname?: string; databasename?: string; isactive?: boolean }): Promise<any> {
        return this.put(`/company-management/companies/${id}`, payload);
    }

    async deleteCompany(id: number): Promise<any> {
        return this.delete(`/company-management/companies/${id}`);
    }

    async getUserCompanies(userId: number): Promise<any> {
        return this.get(`/company-management/users/${userId}/companies`);
    }

  async addUserCompany(userId: number, payload: { companyid: number; isprimary?: boolean }): Promise<any> {
    return this.post(`/company-management/users/${userId}/companies`, payload);
  }

  async removeUserCompany(userId: number, companyId: number): Promise<any> {
    return this.delete(`/company-management/users/${userId}/companies/${companyId}`);
  }

  async getUserProjects(userId: number): Promise<any> {
    return this.get(`/company-management/users/${userId}/projects`);
  }

  async addUserProject(userId: number, projectCode: string): Promise<any> {
    return this.post(`/company-management/users/${userId}/projects`, { projectcode: projectCode });
  }

  async addUserProjects(userId: number, projectCodes: string[]): Promise<any> {
    return this.post(`/company-management/users/${userId}/projects/bulk`, { projectcodes: projectCodes });
  }

  async removeUserProject(userId: number, projectCode: string): Promise<any> {
    return this.delete(`/company-management/users/${userId}/projects/${encodeURIComponent(projectCode)}`);
  }

  async removeUserProjects(userId: number, projectCodes: string[]): Promise<any> {
    return this.delete(`/company-management/users/${userId}/projects/bulk`, { data: { projectcodes: projectCodes } });
  }

  async grantAllProjectsToUser(userId: number): Promise<any> {
    return this.post(`/company-management/users/${userId}/projects/grant-all`, {});
  }

  async getUserCostCenters(userId: number): Promise<any> {
    return this.get(`/company-management/users/${userId}/cost-centers`);
  }

  async addUserCostCenter(userId: number, costCenterCode: string): Promise<any> {
    return this.post(`/company-management/users/${userId}/cost-centers`, { costcenter: costCenterCode });
  }

  async addUserCostCenters(userId: number, costCenterCodes: string[]): Promise<any> {
    return this.post(`/company-management/users/${userId}/cost-centers/bulk`, { costcenters: costCenterCodes });
  }

  async removeUserCostCenter(userId: number, costCenterCode: string): Promise<any> {
    return this.delete(`/company-management/users/${userId}/cost-centers/${encodeURIComponent(costCenterCode)}`);
  }

  async removeUserCostCenters(userId: number, costCenterCodes: string[]): Promise<any> {
    return this.delete(`/company-management/users/${userId}/cost-centers/bulk`, { data: { costcenters: costCenterCodes } });
  }

  async grantAllCostCentersToUser(userId: number): Promise<any> {
    return this.post(`/company-management/users/${userId}/cost-centers/grant-all`, {});
  }

    async grantAllUserCompanies(userId: number): Promise<any> {
        return this.post(`/company-management/users/${userId}/companies/grant-all`);
    }

    // Access Management: Per-user modules
    async getUserModules(userId: number): Promise<any> {
        return this.get(`/access-management/users/${userId}/modules`);
    }

    async addUserModule(userId: number, modulekey: string): Promise<any> {
        return this.post(`/access-management/users/${userId}/modules`, { modulekey });
    }

    async removeUserModule(userId: number, moduleKey: string): Promise<any> {
        return this.delete(`/access-management/users/${userId}/modules/${moduleKey}`);
    }

    async grantAllUserModules(userId: number): Promise<any> {
        return this.post(`/access-management/users/${userId}/modules/grant-all`);
    }

    // Schedule Jobs methods
    async initSchedules(): Promise<any> {
        return this.post('/schedules/init');
    }

    async getScheduleEndpoints(): Promise<any> {
        return this.get('/schedules/endpoints');
    }

    async getScheduleFields(endpointKey: string, companyId?: number): Promise<any> {
        const params = companyId ? { companyId } : undefined;
        return this.get(`/schedules/endpoints/${endpointKey}/fields`, params);
    }

    async listSchedules(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/schedules', params);
    }

    async createSchedule(payload: {
        name: string;
        companyid?: number | null;
        endpointkey: string;
        selectedfields: string[];
        condition?: string;
        datefield?: string;
        cronexpression: string;
    }): Promise<any> {
        return this.post('/schedules', payload);
    }

    async updateSchedule(id: number, payload: any): Promise<any> {
        return this.put(`/schedules/${id}`, payload);
    }

  async runSchedule(id: number): Promise<any> {
    // Use longer timeout for schedule runs (5 minutes) to handle large datasets
    const response = await this.axiosInstance.post(`/schedules/${id}/run`, {}, { timeout: 300000 });
    return response.data;
  }

  async deleteSchedule(id: number): Promise<any> {
    return this.delete(`/schedules/${id}`);
  }

    async getScheduleRuns(id: number): Promise<any> {
        return this.get(`/schedules/${id}/runs`);
    }

    async getItemsCustom(companyId?: number, fields: string[] = [], filter?: string): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (fields && fields.length) params.fields = fields.join(',');
        if (filter) params.filter = filter;
        return this.get('/sap/items/custom', params);
    }

  async listLocalItemMaster(companyId?: number, fields?: string[], requisitionType?: string, allocationType?: string, activeOnly = false): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (fields && fields.length) params.fields = fields.join(',');
    if (requisitionType) params.requisitionType = requisitionType;
    if (allocationType) params.allocationType = allocationType;
    if (activeOnly) params.activeOnly = true;
        return this.get('/item-master', params);
    }

    async syncLocalItemMaster(scheduleId: number): Promise<any> {
        return this.post('/item-master/sync', { scheduleId });
    }

    async updateLocalItem(id: number, data: Record<string, any>): Promise<any> {
        return this.put(`/item-master/${id}`, { data });
    }

    async deleteLocalItem(id: number): Promise<any> {
        return this.delete(`/item-master/${id}`);
    }

    async createLocalItem(companyid: number | undefined, itemcode: string, data: Record<string, any>): Promise<any> {
        return this.post('/item-master', { companyid, itemcode, data });
    }

    async exportPRWorkflows(): Promise<void> {
        const token = localStorage.getItem('token');
        const url = `${this.axiosInstance.defaults.baseURL}/user-management/pr-workflows/export`;

        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) throw new Error('Export failed');

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `PR_Workflows_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    // Role Management APIs
    async getRoles(includeInactive: boolean = false): Promise<any> {
        return this.get('/roles', { includeInactive });
    }

    async getRoleById(roleId: number): Promise<any> {
        return this.get(`/roles/${roleId}`);
    }

    async getAvailablePermissions(): Promise<any> {
        return this.get('/roles/permissions');
    }

    async getMyPermissions(): Promise<any> {
        return this.get('/roles/my-permissions');
    }

    async createRole(roleData: any): Promise<any> {
        return this.post('/roles', roleData);
    }

    async updateRole(roleId: number, roleData: any): Promise<any> {
        return this.put(`/roles/${roleId}`, roleData);
    }

    async deleteRole(roleId: number): Promise<any> {
        return this.delete(`/roles/${roleId}`);
    }

    // Cost Center Mapping APIs
    async getCostCenterMappings(companyId?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        return this.get('/cost-center-mappings', params);
    }

    async getDistributionRules(companyId?: number, dimension?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        if (dimension) params.dimension = dimension;
        return this.get('/cost-center-mappings/distribution-rules', params);
    }

    async saveCostCenterMapping(mappingData: any): Promise<any> {
        return this.post('/cost-center-mappings', mappingData);
    }

    async batchUpdateCostCenterMappings(mappings: any[], companyId?: number): Promise<any> {
        return this.post('/cost-center-mappings/batch', { mappings, companyId });
    }

    async deleteCostCenterMapping(mappingId: number): Promise<any> {
        return this.delete(`/cost-center-mappings/${mappingId}`);
    }

    // Transaction Sync APIs
    async getTransactionSyncs(companyId?: number): Promise<any> {
        const params: any = {};
        if (companyId) params.companyId = companyId;
        return this.get('/transaction-sync', params);
    }

    async getTransactionSyncById(syncId: number): Promise<any> {
        return this.get(`/transaction-sync/${syncId}`);
    }

    async getAvailableEndpoints(): Promise<any> {
        return this.get('/transaction-sync/endpoints');
    }

    async getEndpointFields(endpoint: string): Promise<any> {
        return this.get(`/transaction-sync/endpoints/${endpoint}/fields`);
    }

    async createTransactionSync(syncData: any): Promise<any> {
        return this.post('/transaction-sync', syncData);
    }

    async updateTransactionSync(syncId: number, syncData: any): Promise<any> {
        return this.put(`/transaction-sync/${syncId}`, syncData);
    }

    async deleteTransactionSync(syncId: number): Promise<any> {
        return this.delete(`/transaction-sync/${syncId}`);
    }

    async executeTransactionSync(syncId: number): Promise<any> {
        const response = await this.axiosInstance.post(`/transaction-sync/${syncId}/execute`, {}, {
            timeout: 300000 // 5 minutes timeout for sync execution
        });
        return response.data;
    }

    async getTransactionSyncLogs(syncId: number, limit?: number): Promise<any> {
        const params: any = {};
        if (limit) params.limit = limit;
        return this.get(`/transaction-sync/${syncId}/logs`, params);
    }

    async getSystemConfig(): Promise<any> {
        return this.get('/settings/system-config');
    }

    async updateSystemConfig(updates: Array<{ key: string; value: string }>): Promise<any> {
        return this.put('/settings/system-config', { updates });
    }

    // Monthly Budget APIs
    async getMonthlyBudgets(params?: any): Promise<any> {
        return this.get('/monthly-budget', params);
    }

    async getMonthlyBudgetById(id: number): Promise<any> {
        return this.get(`/monthly-budget/${id}`);
    }

    async createMonthlyBudget(data: any): Promise<any> {
        return this.post('/monthly-budget', data);
    }

    async updateMonthlyBudget(id: number, data: any): Promise<any> {
        return this.put(`/monthly-budget/${id}`, data);
    }

    async deleteMonthlyBudget(id: number): Promise<any> {
        return this.delete(`/monthly-budget/${id}`);
    }

    async bulkUpsertMonthlyBudgets(budgets: any[]): Promise<any> {
        return this.post('/monthly-budget/bulk/upsert', { budgets });
    }

    async bulkDeleteMonthlyBudgets(ids: number[]): Promise<any> {
        return this.post('/monthly-budget/bulk/delete', { ids });
    }

    async checkLineBudget(data: {
        projectCode: string;
        accountCode?: string;
        fiscalYear: number;
        month: number;
        amount: number;
        currentPRID?: number;
    }): Promise<any> {
        return this.post('/budget-check/check-line', data);
    }

    async checkPRBudget(data: {
        projectCode: string;
        lines: any[];
        fiscalYear: number;
        month: number;
    }): Promise<any> {
        return this.post('/budget-check/check-pr', data);
    }

    async getBudgetProjects(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/projects', params);
    }

    async getBudgetDepartments(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/departments', params);
    }

    async getBudgetCostCodes(params?: any): Promise<any> {
        return this.get('/monthly-budget/data/costcodes', params);
    }

    // PO methods
    async createPO(poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/po', final);
    }

    async createPODraft(poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.post('/po/draft', final);
    }

    async updatePODraft(poId: number, poData: any): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const final = { ...(poData || {}) } as any;
        if (!final.companyid && selectedCompanyId) final.companyid = selectedCompanyId;
        return this.put(`/po/draft/${poId}`, final);
    }

    async discardPODraft(poId: number): Promise<any> {
        return this.delete(`/po/draft/${poId}`);
    }

    async getPO(id: number): Promise<any> {
        return this.get(`/po/${id}`);
    }

    async getMyPOs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/my-pos', params);
    }

    async getMyPODrafts(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/my-drafts', params);
    }

    async getPendingApprovalsPO(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/pending-approvals', params);
    }

    async getApproverPOs(): Promise<any> {
        const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '');
        const params = selectedCompanyId ? { companyId: selectedCompanyId } : {};
        return this.get('/po/approver-tracking', params);
    }

    async submitPODraft(poId: number): Promise<any> {
        return this.post(`/po/${poId}/submit`);
    }

    async movePOToCompleted(poId: number): Promise<any> {
        return this.post(`/po/${poId}/move-to-completed`);
    }

    async processPOApproval(poId: number, action: string, comments?: string): Promise<any> {
        return this.post(`/po/${poId}/process`, { action, comments });
    }

    async getPOWorkflowStatus(poId: number): Promise<any> {
        return this.get(`/po/${poId}/workflow`);
    }

    async getPOApprovalHistory(poId: number): Promise<any> {
        return this.get(`/po/${poId}/history`);
    }

    async resolvePOApprovalActionToken(token: string): Promise<any> {
        return this.get(`/po/email-action/token/${encodeURIComponent(token)}`);
    }

    async processPOApprovalActionToken(token: string, comments?: string): Promise<any> {
        return this.post(`/po/email-action/token/${encodeURIComponent(token)}/process`, { comments });
    }

    async resolvePOBulkApprovalToken(token: string): Promise<any> {
        return this.get(`/po/email-action/bulk/${encodeURIComponent(token)}`);
    }

    async processPOBulkApprovalToken(token: string, payload: { poIds: number[]; action: string; comments?: string }): Promise<any> {
        return this.post(`/po/email-action/bulk/${encodeURIComponent(token)}/process`, payload);
    }

    async uploadPOAttachments(poId: number, files: File[], onUploadProgress?: (e: any) => void): Promise<any> {
        const form = new FormData();
        for (const f of files) form.append('files', f);
        const response = await this.axiosInstance.post(`/po/${poId}/attachments/upload`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
        });
        return response.data;
    }

    async getPOAttachments(poId: number): Promise<any> {
        return this.get(`/po/${poId}/attachments`);
    }

    async deletePOAttachment(poId: number, attachmentId: number): Promise<any> {
        return this.delete(`/po/${poId}/attachments/${attachmentId}`);
    }

    getPOAttachmentDownloadUrl(poId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${API_BASE_URL}/po/${poId}/attachments/${attachmentId}/download${q}`;
    }

    getPOAttachmentViewUrl(poId: number, attachmentId: number): string {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        return `${API_BASE_URL}/po/${poId}/attachments/${attachmentId}/view${q}`;
    }

    subscribePOStatus(poId: number): EventSource {
        const token = localStorage.getItem('token') || '';
        const q = token ? `?token=${encodeURIComponent(token)}` : '';
        const url = `${API_BASE_URL}/po/${poId}/subscribe${q}`;
        const es = new EventSource(url);
        return es;
    }

    async copyFromPR(prId: number): Promise<any> {
        return this.post(`/po/copy-from-pr/${prId}`);
    }

    async globalSearch(query: string, options?: {
        types?: string;
        companyId?: number;
        databaseName?: string;
        limit?: number;
        offset?: number;
    }): Promise<any> {
        const params: any = { q: query };
        if (options) {
            if (options.types) params.types = options.types;
            if (options.companyId) params.companyId = options.companyId;
            if (options.databaseName) params.databaseName = options.databaseName;
            if (options.limit) params.limit = options.limit;
            if (options.offset) params.offset = options.offset;
        }
        return this.get('/search', params);
    }

    async getSearchAutocomplete(query: string, type?: string): Promise<any> {
        const params: any = { q: query };
        if (type) params.type = type;
        return this.get('/search/autocomplete', params);
    }

    async syncSearchIndex(): Promise<any> {
        return this.post('/search/sync');
    }

    async getSearchHealth(): Promise<any> {
        return this.get('/search/health');
    }
}
// Assign instance to a variable to avoid anonymous default export warning
const apiService = new ApiService();
export default apiService;
